import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { BehaviorSubject, Observable, Subscription } from "rxjs";
import { Media } from "../../model/media";
import { Messages } from "../../model/messages";
import { MediaService } from "../../services/media.service";
import { AuthService } from "../../auth.service";
import { Router } from "@angular/router";
import { TicketService } from "../../services/ticket.service";
import { Ticket } from "../../model/ticket";
import { environment } from "../../../environments/environment";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { Navigation } from "../../model/navigation";

@Component({
    selector: "media-details",
    templateUrl: "./media-details.component.html",
    styleUrls: ["./media-details.component.css"],
})
export class MediaDetailsComponent implements OnInit, OnDestroy {
    @Input() currentTicket: Observable<Ticket>;
    @Input() isDisabled: boolean = false;
    @Input() mediaInput: Media;

    @ViewChild("fileInput") fileInput: ElementRef<HTMLInputElement>;

    mediaForm: FormGroup;
    editMode: boolean = false;
    selectedFile: File | null = null;
    media = new BehaviorSubject<Media>(Media.EMPTY);
    messages = new BehaviorSubject<Messages>(Messages.EMPTY);
    imageUrl = new BehaviorSubject<SafeUrl | null>(null);
    isLoading: boolean = false;
    fileInputId: string;

    private ticketValue: Ticket | null = null;
    private subscriptions = new Subscription();

    constructor(
        private fb: FormBuilder,
        private mediaService: MediaService,
        private ticketService: TicketService,
        private auth: AuthService,
        private router: Router,
        private sanitizer: DomSanitizer
    ) { }

    ngOnInit(): void {
        this.fileInputId = `file-upload-${Math.random().toString(36).substr(2, 9)}`;
        this.initializeForm();

        if (this.currentTicket) {
            this.subscriptions.add(
                this.currentTicket.subscribe(t => this.ticketValue = t)
            );
        }

        if (this.mediaInput) {
            this.populateForm(this.mediaInput);
        }

        this.subscriptions.add(
            this.mediaService.selectedMediaObservable.subscribe(media => {
                if (media) this.populateForm(media);
            })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    private initializeForm(): void {
        this.mediaForm = this.fb.group({
            name: ["", Validators.required],
            remark: [""],
        });
    }

    onFileSelected(event: any): void {
        if (this.isDisabled) return;
        const file = event.target.files[0];
        if (file) {
            this.selectedFile = file;
            this.mediaForm.patchValue({ name: file.name });
        }
    }

    populateForm(media: Media): void {
        this.media.next(media);
        this.mediaForm.patchValue({ name: media.name, remark: media.remark });
        this.editMode = media.id !== Navigation.NEW;
        if (this.isImage(media.contentType)) this.previewImage();
    }

    triggerSave(): void {
        if (this.isDisabled) return;

        const ticket = this.ticketValue;
        if (!ticket || ticket.id === null || ticket.id === undefined) return;

        if (!this.mediaForm.valid || (!this.selectedFile && this.media.value.id === Navigation.NEW)) {
            this.messages.next(Messages.from({ status: ["media.error.validation"] }));
            return;
        }

        this.isLoading = true;
        const token = this.auth.token;
        if (!token) return;

        const updatedMedia = { ...this.media.value };
        updatedMedia.name = this.mediaForm.value.name;
        updatedMedia.remark = this.mediaForm.value.remark;

        const formData = new FormData();
        formData.append("name", updatedMedia.name);
        formData.append("ticketId", ticket.id.toString());
        formData.append("remark", updatedMedia.remark || "");

        if (this.selectedFile) {
            formData.append("upload", this.selectedFile, this.selectedFile.name);
        }

        this.mediaService.uploadMedia(token, updatedMedia, formData).subscribe({
            next: (msgs: Messages) => {
                this.isLoading = false;
                this.messages.next(msgs);

                if (!msgs.hasAnyMessages) {
                    this.refreshMediaList(token, ticket.id!);
                    this.clearForm();
                    this.router.navigate([`tickets/${ticket.id}/fragment/media/${updatedMedia.id}`]);
                }
            },
            error: (err: any) => {
                this.isLoading = false;
                console.error("Erro no upload", err);
            }
        });
    }

    public refreshMediaList(token: string, ticketId: number): void {
        this.ticketService.getTicketWithParts(token, ticketId).subscribe({
            next: (data: any) => {
                const ticket = data as Ticket;
                if (ticket?.parts?.media) {
                    this.mediaService.accept(ticket.parts.media);
                }
            },
            error: (err: any) => console.error("Erro ao atualizar lista", err)
        });
    }

    clearForm(): void {
        this.mediaForm.reset();
        this.selectedFile = null;
        this.imageUrl.next(null);
        this.media.next(Media.EMPTY);
        if (this.fileInput) {
            this.fileInput.nativeElement.value = "";
        }
    }

    isImage(contentType: string | undefined | null): boolean {
        return contentType ? contentType.startsWith("image/") : false;
    }

    previewImage(): void {
        const media = this.media.value;
        const ticket = this.ticketValue;
        if (!ticket || !media.fileInfo?.fileName) return;

        const url = `${environment.API_BASE_URL}media/for-ticket/${ticket.id}/download/${media.id}/${media.fileInfo.fileName}`;
        this.mediaService.downloadAuthenticatedMediaUrlAsSafeUrl(url, this.auth.token)
            .then(safeUrl => this.imageUrl.next(safeUrl))
            .catch(() => {});
    }
}